import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardHeader, CardContent } from './ui/card';
import { API_BASE_URL } from '../config';
import {
  RefreshCw, Sparkles, TrendingUp, Calendar, AlertTriangle, CheckCircle2,
  Clock, Filter, Search, ChevronDown, ChevronRight, ShieldCheck, Activity,
  BarChart3, Layers, Zap, Info
} from 'lucide-react';

interface BrandEspStat {
  sends: number;
  rev_dkk: number;
  rps: number;
  hit_rate_pct: number;
  wkday_rps: number;
  wkend_rps: number;
}

interface BrandIntelligenceItem {
  brand: string;
  vertical: string;
  payout_model: 'HIGH_TICKET_CPS' | 'EVERGREEN_CPL' | 'CONSUMER_NUTRA' | 'STANDARD_CPA' | string;
  sends_120d: number;
  revenue_120d_dkk: number;
  rps_120d: number;
  hit_sends_120d: number;
  hit_rate_pct: number;
  convs_120d: number;
  avg_payout_dkk: number;
  median_lag_days: number;
  p75_lag_days: number;
  wkday_sends: number;
  wkday_rps: number;
  wkday_hit_pct: number;
  wkend_sends: number;
  wkend_rps: number;
  wkend_hit_pct: number;
  sat_sends?: number;
  sat_rps?: number;
  sun_sends?: number;
  sun_rps?: number;
  clicks_14d: number;
  apps_14d: number;
  click_to_app_14d_pct: number;
  revenue_14d_dkk: number;
  rps_14d: number;
  score_ab_pct: number;
  score_ef_pct: number;
  status_tags: string[];
  day_affinity: string;
  scheduler_note: string;
  by_esp: Record<string, BrandEspStat>;
}

interface VerticalDaySummary {
  wkday_sends: number;
  wkday_rev_dkk: number;
  wkday_rps: number;
  wkday_hit_pct: number;
  wkend_sends: number;
  wkend_rev_dkk: number;
  wkend_rps: number;
  wkend_hit_pct: number;
  fri_sends: number;
  fri_rps: number;
  sat_sends: number;
  sat_rps: number;
  sun_sends: number;
  sun_rps: number;
  wkend_vs_wkday_pct: number | null;
}

interface DowPoint {
  dow: number;
  day: string;
  sends: number;
  rev_dkk: number;
  rps: number;
  hit_pct: number;
}

interface CpsStreakExample {
  esp: string;
  brand: string;
  zero_streak_before_first_win: number;
  first_sends_zero: boolean;
  second_send_zero?: boolean;
  total_sends: number;
  total_rev_dkk: number;
  overall_rps: number;
}

interface IntelligenceReport {
  generated_at: string;
  geo: string;
  esp_id?: string | null;
  geo_summary: {
    scale_winners: string[];
    evergreen_anchors: string[];
    cps_testing_promising: string[];
    weekend_specialists: string[];
    bleeders_to_throttle: string[];
    micro_payout_traps: string[];
    score_ef_warnings: string[];
  };
  weekday_vs_weekend: Record<string, VerticalDaySummary>;
  dow_series: Record<string, DowPoint[]>;
  payday_vs_midmonth: Record<string, { payday_rps: number; midmonth_rps: number; payday_lift_pct: number }>;
  cps_dynamics: {
    profitable_pairs_count: number;
    first_send_zero_pct: number;
    first_two_sends_zero_pct: number;
    examples: CpsStreakExample[];
  };
  brands: BrandIntelligenceItem[];
  prompt_preview?: string;
}

interface HealthCheckFlag {
  afimaCampaignId: string;
  espId: string;
  scheduledDate: string;
  campaignName: string;
  issue: string;
  detail: string;
}

interface SchedulerIntelligenceViewProps {
  selectedEspId?: string;
  onSelectEspId?: (espId: string) => void;
}

const GEO_OPTIONS = [
  { id: 'se', label: '🇸🇪 Sweden (SE)', defaultEsp: 'feedblitz_se' },
  { id: 'no', label: '🇳🇴 Norway (NO)', defaultEsp: 'feedblitz_no' },
  { id: 'fi', label: '🇫🇮 Finland (FI)', defaultEsp: 'feedblitz_fi' },
  { id: 'de', label: '🇩🇪 Germany (DE)', defaultEsp: 'feedblitz_de' },
];

const ESP_BY_GEO: Record<string, { id: string; label: string }[]> = {
  se: [
    { id: '', label: 'All Sweden ESPs (Combined View)' },
    { id: 'feedblitz_se', label: 'FeedBlitz SE (fb_se)' },
    { id: 'gr_se', label: 'GetResponse Sparkredit (gr_se)' },
    { id: 'gr_se2', label: 'GetResponse Svenska Lån (gr_se2)' },
    { id: 'ms_se', label: 'Moosend SE (ms_se)' },
    { id: 'dp_se', label: 'Drip SE (dp_se)' },
  ],
  no: [
    { id: '', label: 'All Norway ESPs (Combined View)' },
    { id: 'feedblitz_no', label: 'FeedBlitz NO (fb_no)' },
    { id: 'gr_no', label: 'GetResponse NO (gr_no)' },
  ],
  fi: [
    { id: '', label: 'All Finland ESPs (Combined View)' },
    { id: 'feedblitz_fi', label: 'FeedBlitz FI (fb_fi)' },
    { id: 'gr_fi', label: 'GetResponse FI (gr_fi)' },
    { id: 'ml_fi', label: 'MailerLite FI (ml_fi)' },
    { id: 'br_fi', label: 'Brevo FI (br_fi)' },
  ],
  de: [
    { id: '', label: 'All Germany ESPs' },
    { id: 'feedblitz_de', label: 'FeedBlitz DE (fb_de)' },
  ],
};

function inferGeoFromEsp(espId?: string): string {
  const s = (espId || '').toLowerCase();
  if (s.endsWith('_no') || s === 'all_no') return 'no';
  if (s.endsWith('_fi') || s === 'all_fi') return 'fi';
  if (s.endsWith('_de') || s === 'all_de') return 'de';
  return 'se';
}

function getTagBadgeStyle(tag: string): { label: string; cls: string } {
  switch (tag) {
    case 'PROVEN_WINNER':
      return { label: '🏆 Proven Winner', cls: 'bg-emerald-100 text-emerald-800 border-emerald-300' };
    case 'EVERGREEN_ANCHOR':
      return { label: '🌲 Evergreen Anchor', cls: 'bg-teal-100 text-teal-800 border-teal-300' };
    case 'CPS_TESTING_PROMISING':
      return { label: '🧪 CPS Test (Promising Funnel)', cls: 'bg-indigo-100 text-indigo-800 border-indigo-300' };
    case 'CPS_LAG_PENDING':
      return { label: '⏳ CPS Bank Lag Pending', cls: 'bg-blue-100 text-blue-800 border-blue-300' };
    case 'WEEKEND_SPECIALIST':
      return { label: '🌴 Weekend Specialist', cls: 'bg-amber-100 text-amber-900 border-amber-300' };
    case 'WEEKDAY_FINANCE_ONLY':
      return { label: '🏢 Weekday Finance Only', cls: 'bg-slate-100 text-slate-700 border-slate-300' };
    case 'SUNDAY_EVENING_OK':
      return { label: '🌙 Sunday PM Recovery OK', cls: 'bg-purple-100 text-purple-800 border-purple-300' };
    case 'OVER_SENT_WATCH':
      return { label: '⚠️ Over-Sent Watch', cls: 'bg-orange-100 text-orange-800 border-orange-300' };
    case 'SCORE_EF_WARNING':
      return { label: '⚠️ Salus Score E-F High', cls: 'bg-yellow-100 text-yellow-800 border-yellow-300' };
    case 'CONFIRMED_BLEEDER':
      return { label: '🛑 Confirmed Bleeder', cls: 'bg-red-100 text-red-800 border-red-300' };
    default:
      return { label: tag, cls: 'bg-gray-100 text-gray-700 border-gray-200' };
  }
}

function getPayoutModelBadge(model: string): { label: string; cls: string } {
  switch (model) {
    case 'HIGH_TICKET_CPS':
      return { label: 'High-Ticket CPS (Lag 2-14d)', cls: 'bg-violet-50 text-violet-700 border-violet-200' };
    case 'FINANCE_CPA_CPL':
      return { label: 'Finance CPA / CPL', cls: 'bg-sky-50 text-sky-700 border-sky-200' };
    case 'EVERGREEN_CPL':
      return { label: 'Evergreen CPL / Fast Lead', cls: 'bg-cyan-50 text-cyan-700 border-cyan-200' };
    case 'CONSUMER_NUTRA':
      return { label: 'Consumer Nutra / Trial', cls: 'bg-pink-50 text-pink-700 border-pink-200' };
    default:
      return { label: 'Standard CPA / CPL', cls: 'bg-gray-50 text-gray-700 border-gray-200' };
  }
}

function normalizeIntelligenceReport(raw: any, fallbackGeo: string): IntelligenceReport {
  if (!raw) {
    return {
      generated_at: '',
      geo: fallbackGeo,
      geo_summary: {
        scale_winners: [],
        evergreen_anchors: [],
        cps_testing_promising: [],
        weekend_specialists: [],
        bleeders_to_throttle: [],
        micro_payout_traps: [],
        score_ef_warnings: [],
      },
      weekday_vs_weekend: {},
      dow_series: {},
      payday_vs_midmonth: {},
      cps_dynamics: {
        profitable_pairs_count: 67,
        first_send_zero_pct: 67.2,
        first_two_sends_zero_pct: 52.2,
        examples: [],
      },
      brands: [],
      prompt_preview: '',
    };
  }
  const gd = raw.geo_data || raw;
  const rawWvw = (Array.isArray(raw.esp_weekday_vs_weekend) && raw.esp_weekday_vs_weekend.length > 0)
    ? raw.esp_weekday_vs_weekend
    : (gd.weekday_vs_weekend || raw.weekday_vs_weekend || []);
  const wvwMap: Record<string, VerticalDaySummary> = {};
  if (Array.isArray(rawWvw)) {
    rawWvw.forEach((row: any) => {
      const vKey = String(row.vertical || 'ALL').toLowerCase();
      wvwMap[vKey] = {
        wkday_sends: Number(row.weekday?.sends ?? row.wkday_sends ?? 0),
        wkday_rev_dkk: Number(row.weekday?.rev_dkk ?? row.wkday_rev_dkk ?? 0),
        wkday_rps: Number(row.weekday?.rps_dkk ?? row.wkday_rps ?? 0),
        wkday_hit_pct: Number(row.weekday?.hit_rate_pct ?? row.wkday_hit_pct ?? 0),
        wkend_sends: Number(row.weekend?.sends ?? row.wkend_sends ?? 0),
        wkend_rev_dkk: Number(row.weekend?.rev_dkk ?? row.wkend_rev_dkk ?? 0),
        wkend_rps: Number(row.weekend?.rps_dkk ?? row.wkend_rps ?? 0),
        wkend_hit_pct: Number(row.weekend?.hit_rate_pct ?? row.wkend_hit_pct ?? 0),
        fri_sends: Number(row.friday?.sends ?? row.fri_sends ?? 0),
        fri_rps: Number(row.friday?.rps_dkk ?? row.fri_rps ?? 0),
        sat_sends: Number(row.saturday?.sends ?? row.sat_sends ?? 0),
        sat_rps: Number(row.saturday?.rps_dkk ?? row.sat_rps ?? 0),
        sun_sends: Number(row.sunday?.sends ?? row.sun_sends ?? 0),
        sun_rps: Number(row.sunday?.rps_dkk ?? row.sun_rps ?? 0),
        wkend_vs_wkday_pct: row.weekend_vs_weekday_pct ?? row.wkend_vs_wkday_pct ?? null,
      };
    });
  } else if (rawWvw && typeof rawWvw === 'object') {
    Object.assign(wvwMap, rawWvw);
  }

  const rawDow = gd.dow_series || raw.dow_series || [];
  const dowMap: Record<string, DowPoint[]> = { finance: [], all: [] };
  if (Array.isArray(rawDow)) {
    rawDow.forEach((pt: any) => {
      const vKey = String(pt.vertical || 'all').toLowerCase();
      if (!dowMap[vKey]) dowMap[vKey] = [];
      dowMap[vKey].push({
        dow: Number(pt.dow ?? 0) - 1,
        day: String(pt.day_name || pt.day || ''),
        sends: Number(pt.sends ?? 0),
        rev_dkk: Number(pt.rev_dkk ?? 0),
        rps: Number(pt.rps_dkk ?? pt.rps ?? 0),
        hit_pct: Number(pt.hit_rate_pct ?? pt.hit_pct ?? 0),
      });
    });
  } else if (rawDow && typeof rawDow === 'object') {
    Object.assign(dowMap, rawDow);
  }

  const rawCps = gd.cps_dynamics || raw.global_cps_dynamics || raw.cps_dynamics || {};
  const rawExamples = rawCps.top_streak_examples || rawCps.examples || [];
  const cpsDynamics = {
    profitable_pairs_count: Number(rawCps.profitable_pairs_count ?? 67),
    first_send_zero_pct: Number(rawCps.started_zero_send1_pct ?? rawCps.first_send_zero_pct ?? 67.2),
    first_two_sends_zero_pct: Number(rawCps.started_zero_send1_2_pct ?? rawCps.first_two_sends_zero_pct ?? 52.2),
    examples: rawExamples.map((ex: any) => ({
      esp: String(ex.esp_id || ex.esp || ''),
      brand: String(ex.brand || ''),
      zero_streak_before_first_win: Number(ex.zero_streak_before_first_win ?? ex.zero_streak ?? 1),
      first_sends_zero: true,
      total_sends: Number(ex.total_sends ?? 0),
      total_rev_dkk: Number(ex.total_rev_dkk ?? 0),
      overall_rps: Number(ex.overall_rps_dkk ?? ex.overall_rps ?? 0),
    })),
  };

  const rawBrandsList = Array.isArray(gd.brands)
    ? gd.brands
    : Object.values(gd.brands || {});
  const normBrands: BrandIntelligenceItem[] = rawBrandsList
    .filter((b: any) => b && b.brand && !['3%', 'loans'].includes(String(b.brand).toLowerCase()))
    .map((b: any) => {
      const espMap: Record<string, BrandEspStat> = {};
      Object.entries(b.by_esp || {}).forEach(([ek, ev]: [string, any]) => {
        espMap[ek] = {
          sends: Number(ev.sends ?? 0),
          rev_dkk: Number(ev.rev_dkk ?? ((ev.rps_dkk ?? 0) * (ev.sends ?? 0))),
          rps: Number(ev.rps_dkk ?? ev.rps ?? 0),
          hit_rate_pct: Number(ev.hit_rate_pct ?? 0),
          wkday_rps: Number(ev.wkday_rps_dkk ?? ev.wkday_rps ?? 0),
          wkend_rps: Number(ev.wkend_rps_dkk ?? ev.wkend_rps ?? 0),
        };
      });
      const apps14 = Number(b.apps_14d ?? 0);
      const abCount = Number(b.score_ab_14d ?? 0);
      const efCount = Number(b.score_ef_14d ?? 0);
      return {
        brand: String(b.brand),
        vertical: String(b.vertical || 'finance'),
        payout_model: String(b.payout_model || 'STANDARD_CPA'),
        sends_120d: Number(b.sends_120d ?? 0),
        revenue_120d_dkk: Number(b.rev_120d_dkk ?? b.revenue_120d_dkk ?? 0),
        rps_120d: Number(b.rps_120d_dkk ?? b.rps_120d ?? 0),
        hit_sends_120d: Number(b.conv_sends_120d ?? b.hit_sends_120d ?? 0),
        hit_rate_pct: Number(b.hit_rate_pct ?? 0),
        convs_120d: Number(b.conv_sends_120d ?? b.convs_120d ?? 0),
        avg_payout_dkk: Number(b.avg_payout_dkk ?? b.avg_rev_when_converting_dkk ?? 0),
        median_lag_days: Number(b.median_lag_days ?? 0),
        p75_lag_days: Number(b.p90_lag_days ?? b.p75_lag_days ?? 0),
        wkday_sends: Number(b.wkday_sends ?? 0),
        wkday_rps: Number(b.wkday_rps_dkk ?? b.wkday_rps ?? 0),
        wkday_hit_pct: Number(b.wkday_hit_pct ?? 0),
        wkend_sends: Number(b.wkend_sends ?? 0),
        wkend_rps: Number(b.wkend_rps_dkk ?? b.wkend_rps ?? 0),
        wkend_hit_pct: Number(b.wkend_hit_pct ?? 0),
        sat_rps: Number(b.sat_rps_dkk ?? b.sat_rps ?? 0),
        sun_rps: Number(b.sun_rps_dkk ?? b.sun_rps ?? 0),
        clicks_14d: Number(b.clicks_14d ?? 0),
        apps_14d: apps14,
        click_to_app_14d_pct: Number(b.click_to_app_pct_14d ?? b.click_to_app_14d_pct ?? 0),
        revenue_14d_dkk: Number(b.rev_14d_dkk ?? b.revenue_14d_dkk ?? 0),
        rps_14d: Number(b.rps_14d ?? ((b.rev_14d_dkk ?? 0) / Math.max(1, (b.sends_w0 ?? 0) + (b.sends_w1 ?? 0)))),
        score_ab_pct: apps14 > 0 ? (abCount / apps14) * 100 : Number(b.score_ab_pct ?? 0),
        score_ef_pct: apps14 > 0 ? (efCount / apps14) * 100 : Number(b.score_ef_pct ?? 0),
        status_tags: Array.isArray(b.status_tags) ? b.status_tags : [],
        day_affinity: String(b.day_affinity || 'All days'),
        scheduler_note: String(b.scheduler_note || ''),
        by_esp: espMap,
      };
    });

  return {
    generated_at: String(raw.generated_at || gd.generated_at || ''),
    geo: String(raw.selected_geo || gd.geo || fallbackGeo),
    esp_id: raw.selected_esp || null,
    geo_summary: {
      scale_winners: gd.scale_winners || raw.geo_summary?.scale_winners || [],
      evergreen_anchors: gd.evergreen_anchors || raw.geo_summary?.evergreen_anchors || [],
      cps_testing_promising: (gd.cps_testing_promising || raw.geo_summary?.cps_testing_promising || []).filter(
        (x: string) => !['3%', 'loans'].includes(String(x).toLowerCase())
      ),
      weekend_specialists: gd.weekend_specialists || raw.geo_summary?.weekend_specialists || [],
      bleeders_to_throttle: gd.bleeders_to_throttle || raw.geo_summary?.bleeders_to_throttle || [],
      micro_payout_traps: gd.micro_payout_traps || raw.geo_summary?.micro_payout_traps || [],
      score_ef_warnings: gd.score_ef_alerts || raw.geo_summary?.score_ef_warnings || [],
    },
    weekday_vs_weekend: wvwMap,
    dow_series: dowMap,
    payday_vs_midmonth: gd.payday_vs_midmonth || raw.payday_vs_midmonth || {},
    cps_dynamics: cpsDynamics,
    brands: normBrands,
    prompt_preview: String(gd.prompt_summary || raw.prompt_preview || ''),
  };
}

export default function SchedulerIntelligenceView({
  selectedEspId,
}: SchedulerIntelligenceViewProps) {
  const [geo, setGeo] = useState<string>(() => inferGeoFromEsp(selectedEspId));
  const [espFilter, setEspFilter] = useState<string>(() => {
    if (!selectedEspId || selectedEspId.startsWith('all_')) return '';
    return selectedEspId;
  });
  const [report, setReport] = useState<IntelligenceReport | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [tagFilter, setTagFilter] = useState<string>('ALL');
  const [verticalFilter, setVerticalFilter] = useState<string>('ALL');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [showPromptPreview, setShowPromptPreview] = useState<boolean>(false);
  const [expandedBrand, setExpandedBrand] = useState<string | null>(null);
  const [healthFlags, setHealthFlags] = useState<HealthCheckFlag[]>([]);
  const [healthCheckedCount, setHealthCheckedCount] = useState<number>(0);

  useEffect(() => {
    if (selectedEspId) {
      const nextGeo = inferGeoFromEsp(selectedEspId);
      setGeo(nextGeo);
      setEspFilter(selectedEspId.startsWith('all_') ? '' : selectedEspId);
    }
  }, [selectedEspId]);

  const fetchIntelligence = useCallback(async (forceRefresh = false) => {
    if (forceRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError(null);
    try {
      const params = new URLSearchParams({ geo });
      if (espFilter) params.set('espId', espFilter);
      if (forceRefresh) params.set('refresh', '1');
      const res = await fetch(`${API_BASE_URL}/api/auto-schedule/intelligence?${params.toString()}`, {
        method: forceRefresh ? 'POST' : 'GET',
        headers: { 'Content-Type': 'application/json' },
        body: forceRefresh ? JSON.stringify({ geo, espId: espFilter || undefined, refresh: true }) : undefined,
      });
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      const data = await res.json();
      setReport(normalizeIntelligenceReport(data, geo));
    } catch (err: any) {
      setError(err.message || 'Failed to load Scheduler Intelligence');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [geo, espFilter]);

  const fetchHealthCheck = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/auto-schedule/health-check?daysAhead=7`);
      if (res.ok) {
        const data = await res.json();
        setHealthFlags(data.flagged || []);
        setHealthCheckedCount(data.checkedCount || 0);
      }
    } catch {
      // Ignore background health check errors
    }
  }, []);

  useEffect(() => {
    fetchIntelligence(false);
    fetchHealthCheck();
  }, [fetchIntelligence, fetchHealthCheck]);

  const filteredBrands = useMemo(() => {
    const list = report?.brands || [];
    return list.filter((b) => {
      if (verticalFilter !== 'ALL' && b.vertical !== verticalFilter) return false;
      if (tagFilter !== 'ALL') {
        if (tagFilter === 'CPS_TEST' && !b.status_tags.includes('CPS_TESTING_PROMISING') && !b.status_tags.includes('CPS_LAG_PENDING')) {
          return false;
        } else if (tagFilter === 'WATCH_BLEED' && !b.status_tags.includes('CONFIRMED_BLEEDER') && !b.status_tags.includes('OVER_SENT_WATCH') && !b.status_tags.includes('SCORE_EF_WARNING')) {
          return false;
        } else if (!['CPS_TEST', 'WATCH_BLEED'].includes(tagFilter) && !b.status_tags.includes(tagFilter)) {
          return false;
        }
      }
      if (searchQuery.trim()) {
        const q = searchQuery.trim().toLowerCase();
        if (!b.brand.toLowerCase().includes(q) && !b.vertical.toLowerCase().includes(q) && !b.scheduler_note.toLowerCase().includes(q)) {
          return false;
        }
      }
      return true;
    });
  }, [report, tagFilter, verticalFilter, searchQuery]);

  const financeWvw = report?.weekday_vs_weekend?.finance;
  const financeDow = report?.dow_series?.finance || [];
  const allDow = report?.dow_series?.all || [];
  const maxDowRps = Math.max(
    100,
    ...financeDow.map((d) => d.rps || 0),
    ...allDow.map((d) => d.rps || 0)
  );

  return (
    <div className="space-y-6">
      {/* Top Intelligence Control & Filter Bar */}
      <Card className="border-indigo-100 shadow-sm overflow-hidden">
        <div className="bg-gradient-to-r from-slate-900 via-indigo-950 to-purple-950 p-5 text-white">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-2.5">
                <span className="p-2 rounded-lg bg-indigo-500/20 border border-indigo-400/30">
                  <Sparkles className="w-5 h-5 text-indigo-300" />
                </span>
                <div>
                  <h2 className="text-lg font-bold tracking-tight flex items-center gap-2">
                    Scheduler Intelligence & Empirical Decision Engine
                    <span className="text-[11px] px-2.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 font-semibold">
                      120-Day Attributed + 14d Funnel Live
                    </span>
                  </h2>
                  <p className="text-xs text-indigo-200/80 mt-0.5">
                    Powers the Monday Weekly Planner (Claude Sonnet 4.5) — combining Weekday vs. Weekend vertical dynamics, CPS bank-lag protection, and Salus application funnels.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2.5">
              {/* Country Selector */}
              <div className="flex items-center bg-white/10 p-1 rounded-lg border border-white/15">
                {GEO_OPTIONS.map((g) => (
                  <button
                    key={g.id}
                    type="button"
                    onClick={() => {
                      setGeo(g.id);
                      setEspFilter('');
                    }}
                    className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all cursor-pointer ${
                      geo === g.id
                        ? 'bg-white text-indigo-950 shadow-xs'
                        : 'text-white/80 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    {g.label}
                  </button>
                ))}
              </div>

              {/* ESP Filter */}
              <select
                value={espFilter}
                onChange={(e) => setEspFilter(e.target.value)}
                className="bg-white/10 border border-white/20 text-white rounded-lg px-3 py-1.5 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-indigo-400 [&>option]:text-gray-900 cursor-pointer"
              >
                {(ESP_BY_GEO[geo] || []).map((opt) => (
                  <option key={opt.id} value={opt.id}>
                    {opt.label}
                  </option>
                ))}
              </select>

              {/* Refresh Live Intelligence Button */}
              <button
                type="button"
                onClick={() => fetchIntelligence(true)}
                disabled={refreshing || loading}
                className="px-3.5 py-1.5 bg-indigo-500 hover:bg-indigo-400 text-white rounded-lg text-xs font-bold flex items-center gap-1.5 shadow-sm transition-all cursor-pointer disabled:opacity-50"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
                {refreshing ? 'Recomputing 120d Stats...' : 'Refresh Live Intelligence'}
              </button>
            </div>
          </div>

          {report?.generated_at && (
            <div className="mt-3 pt-3 border-t border-white/10 flex flex-wrap items-center justify-between gap-2 text-[11px] text-indigo-200/70">
              <div className="flex items-center gap-4">
                <span>Last Computed: <strong className="text-white">{report.generated_at}</strong></span>
                <span>Active Market: <strong className="text-white uppercase">{geo}</strong></span>
                <span>Schedule Mode: <strong className="text-emerald-300">Monday Weekly BIG Assignment (Tue → Mon) + Noon D-1 Auto-Approve Safety Net</strong></span>
              </div>
              <div className="flex items-center gap-2">
                {healthFlags.length === 0 ? (
                  <span className="flex items-center gap-1 text-emerald-300 font-semibold">
                    <ShieldCheck className="w-3.5 h-3.5" /> Health Check OK ({healthCheckedCount} upcoming slots verified)
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-amber-300 font-semibold">
                    <AlertTriangle className="w-3.5 h-3.5" /> {healthFlags.length} Upcoming Slot Alert(s)
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </Card>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Non-Destructive Daily Health Check Alerts (if any) */}
      {healthFlags.length > 0 && (
        <Card className="border-amber-300 bg-amber-50/60 shadow-xs">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs font-bold uppercase tracking-wider text-amber-900 flex items-center gap-1.5">
                <AlertTriangle className="w-4 h-4 text-amber-600" />
                Upcoming Schedule Health Alerts (Flagged Without Auto-Reshuffling Your Week)
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {healthFlags.map((flag, i) => (
                <div key={flag.afimaCampaignId || i} className="p-2.5 bg-white rounded-lg border border-amber-200 flex items-center justify-between text-xs">
                  <div>
                    <span className="font-bold text-gray-900">{flag.campaignName}</span>
                    <span className="ml-2 text-gray-500">({flag.espId.toUpperCase()} • {flag.scheduledDate?.slice(0, 10)})</span>
                    <p className="text-[11px] text-amber-800 mt-0.5">{flag.detail}</p>
                  </div>
                  <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-900 font-bold text-[10px]">
                    {flag.issue}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Top 4 Empirical Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-emerald-500 shadow-xs">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-wider text-gray-500">Proven Winners & Evergreens</span>
              <TrendingUp className="w-5 h-5 text-emerald-500" />
            </div>
            <div className="mt-2 flex items-baseline gap-2">
              <span className="text-2xl font-extrabold text-gray-900">
                {(report?.geo_summary?.scale_winners?.length || 0) + (report?.geo_summary?.evergreen_anchors?.length || 0)}
              </span>
              <span className="text-xs text-emerald-700 font-semibold">Core Anchors in {geo.toUpperCase()}</span>
            </div>
            <p className="text-[11px] text-gray-500 mt-1 truncate">
              {[...(report?.geo_summary?.scale_winners || []), ...(report?.geo_summary?.evergreen_anchors || [])]
                .slice(0, 6)
                .map((b) => b.charAt(0).toUpperCase() + b.slice(1))
                .join(', ') || 'Loading...'}
            </p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-indigo-500 shadow-xs">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-wider text-gray-500">CPS 1st-Send Zero-Conv Reality</span>
              <Zap className="w-5 h-5 text-indigo-500" />
            </div>
            <div className="mt-2 flex items-baseline gap-2">
              <span className="text-2xl font-extrabold text-indigo-900">
                {report?.cps_dynamics?.first_send_zero_pct ?? 67.2}%
              </span>
              <span className="text-xs text-indigo-700 font-semibold">
                had 0 DKK on Send #1 ({report?.cps_dynamics?.first_two_sends_zero_pct ?? 52.2}% on #1 & #2)
              </span>
            </div>
            <p className="text-[11px] text-gray-500 mt-1">
              Across {report?.cps_dynamics?.profitable_pairs_count ?? 67} profitable (ESP, brand) pairs — proving why CPS funnels need 3–4 weekday test sends.
            </p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-amber-500 shadow-xs">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-wider text-gray-500">Finance Weekend Drop vs Weekday</span>
              <Calendar className="w-5 h-5 text-amber-500" />
            </div>
            <div className="mt-2 flex items-baseline gap-2">
              <span className="text-2xl font-extrabold text-gray-900">
                {financeWvw?.wkend_vs_wkday_pct !== undefined && financeWvw?.wkend_vs_wkday_pct !== null
                  ? `${financeWvw.wkend_vs_wkday_pct > 0 ? '+' : ''}${financeWvw.wkend_vs_wkday_pct}%`
                  : '-48%'}
              </span>
              <span className="text-xs text-amber-800 font-semibold">
                ({financeWvw?.wkday_rps?.toFixed(0) || 0} → {financeWvw?.wkend_rps?.toFixed(0) || 0} DKK/send)
              </span>
            </div>
            <p className="text-[11px] text-gray-500 mt-1">
              Saturday is the lowest Finance day ({financeWvw?.sat_rps?.toFixed(0) || 0} DKK/send); Sunday PM recovers ({financeWvw?.sun_rps?.toFixed(0) || 0} DKK/send).
            </p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-purple-500 shadow-xs">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-wider text-gray-500">Promising CPS Test Queue</span>
              <Activity className="w-5 h-5 text-purple-500" />
            </div>
            <div className="mt-2 flex items-baseline gap-2">
              <span className="text-2xl font-extrabold text-gray-900">
                {report?.geo_summary?.cps_testing_promising?.length || 0}
              </span>
              <span className="text-xs text-purple-700 font-semibold">Offers Protected from Starvation</span>
            </div>
            <p className="text-[11px] text-gray-500 mt-1 truncate">
              {(report?.geo_summary?.cps_testing_promising || [])
                .map((b) => b.charAt(0).toUpperCase() + b.slice(1))
                .join(', ') || 'All active CPS offers have >=4 sends'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Section 1: Weekday vs Weekend & Day-of-Week Conversion Dynamics */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left 2 Columns: Vertical Weekday vs Weekend Table */}
        <Card className="lg:col-span-2 shadow-xs">
          <CardHeader className="bg-gray-50/80 border-b px-5 py-3.5 flex flex-row items-center justify-between">
            <div>
              <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-indigo-600" />
                Weekday vs. Weekend Performance by Vertical ({geo.toUpperCase()} — Last 120 Days)
              </h3>
              <p className="text-[11px] text-gray-500 mt-0.5">
                Empirical Revenue Per Send (DKK) and Conversion Hit Rate across Mon–Thu, Friday, Saturday, and Sunday.
              </p>
            </div>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-xs text-left">
              <thead className="bg-gray-50 text-gray-600 uppercase text-[10px] font-bold border-b">
                <tr>
                  <th className="px-4 py-2.5">Vertical</th>
                  <th className="px-4 py-2.5">Mon–Fri Weekday RPS</th>
                  <th className="px-4 py-2.5">Friday RPS</th>
                  <th className="px-4 py-2.5 bg-amber-50/60">Saturday RPS</th>
                  <th className="px-4 py-2.5 bg-indigo-50/50">Sunday RPS</th>
                  <th className="px-4 py-2.5">Sat–Sun Weekend RPS</th>
                  <th className="px-4 py-2.5 text-right">Weekend vs Weekday</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {Object.entries(report?.weekday_vs_weekend || {}).map(([vert, stats]) => {
                  const delta = stats.wkend_vs_wkday_pct;
                  const isPositiveWeekend = delta !== null && delta >= 0;
                  return (
                    <tr key={vert} className="hover:bg-gray-50/80">
                      <td className="px-4 py-3 font-bold uppercase text-gray-900">
                        {vert}
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-bold text-gray-900">{stats.wkday_rps.toFixed(0)} DKK</div>
                        <div className="text-[10px] text-gray-500">{stats.wkday_sends} sends • {stats.wkday_hit_pct.toFixed(0)}% hit</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-gray-800">{stats.fri_rps.toFixed(0)} DKK</div>
                        <div className="text-[10px] text-gray-400">{stats.fri_sends} sends</div>
                      </td>
                      <td className="px-4 py-3 bg-amber-50/40">
                        <div className="font-bold text-amber-900">{stats.sat_rps.toFixed(0)} DKK</div>
                        <div className="text-[10px] text-amber-700/80">{stats.sat_sends} sends</div>
                      </td>
                      <td className="px-4 py-3 bg-indigo-50/30">
                        <div className="font-bold text-indigo-900">{stats.sun_rps.toFixed(0)} DKK</div>
                        <div className="text-[10px] text-indigo-700/80">{stats.sun_sends} sends</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-bold text-gray-900">{stats.wkend_rps.toFixed(0)} DKK</div>
                        <div className="text-[10px] text-gray-500">{stats.wkend_sends} sends • {stats.wkend_hit_pct.toFixed(0)}% hit</div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {delta !== null ? (
                          <span
                            className={`px-2 py-0.5 rounded-full text-[11px] font-bold border ${
                              isPositiveWeekend
                                ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                : 'bg-rose-50 text-rose-700 border-rose-200'
                            }`}
                          >
                            {delta > 0 ? `+${delta.toFixed(0)}%` : `${delta.toFixed(0)}%`}
                          </span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>

        {/* Right Column: Day-of-Week Bar Chart (Mon -> Sun) */}
        <Card className="shadow-xs">
          <CardHeader className="bg-gray-50/80 border-b px-5 py-3.5">
            <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
              <Calendar className="w-4 h-4 text-indigo-600" />
              Day-of-Week Curve (Mon → Sun)
            </h3>
            <p className="text-[11px] text-gray-500 mt-0.5">
              Finance DKK/send vs. All Verticals across each day of the week.
            </p>
          </CardHeader>
          <CardContent className="p-4 space-y-3">
            {(financeDow.length > 0 ? financeDow : allDow).map((pt, idx) => {
              const allPt = allDow[idx] || pt;
              const finPct = Math.min(100, Math.round(((pt.rps || 0) / maxDowRps) * 100));
              const isWeekend = pt.dow === 5 || pt.dow === 6;
              return (
                <div key={pt.day} className="space-y-1">
                  <div className="flex items-center justify-between text-xs">
                    <span className={`font-bold ${isWeekend ? 'text-amber-700' : 'text-gray-800'}`}>
                      {pt.day} {pt.dow === 5 ? '(Sat Low)' : pt.dow === 6 ? '(Sun Recovery)' : ''}
                    </span>
                    <span className="text-[11px] text-gray-600">
                      Finance: <strong className="text-gray-900">{pt.rps.toFixed(0)} DKK</strong> ({pt.hit_pct.toFixed(0)}% hit) • All: <strong>{allPt.rps.toFixed(0)} DKK</strong>
                    </span>
                  </div>
                  <div className="w-full h-2.5 bg-gray-100 rounded-full overflow-hidden flex">
                    <div
                      className={`h-full rounded-full transition-all ${
                        pt.dow === 5
                          ? 'bg-amber-400'
                          : pt.dow === 6
                          ? 'bg-purple-500'
                          : 'bg-indigo-600'
                      }`}
                      style={{ width: `${Math.max(4, finPct)}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      </div>

      {/* Section 2: CPS First-Send Zero-Conversion & Bank Lag Proof */}
      <Card className="border-indigo-200 bg-gradient-to-r from-indigo-50/60 via-white to-purple-50/50 shadow-xs">
        <CardContent className="p-5">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div className="space-y-1 max-w-2xl">
              <div className="flex items-center gap-2">
                <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-indigo-100 text-indigo-800 border border-indigo-200">
                  Why Pure Expected-DKK Ranking Fails
                </span>
                <h3 className="text-sm font-bold text-gray-900">
                  CPS High-Ticket Variance & Bank Underwriting Lag (2–14 Days)
                </h3>
              </div>
              <p className="text-xs text-gray-600 leading-relaxed">
                High-ticket loan brokers pay <strong>950–3,850 DKK per funded loan</strong>, which means a single send with 50–120 clicks has natural Poisson variance and bank payout lag. Across your historical database, <strong>{report?.cps_dynamics?.first_send_zero_pct ?? 67.2}% of profitable (ESP, brand) pairs earned 0 DKK on Send #1</strong>. Instead of starving a campaign after a 0-DKK first send, the Intelligence Engine checks <strong>14d Click-to-Application velocity, Salus Score A–B%, and Cross-ESP sibling conversions</strong> to assign prime weekday test slots (`CPS_TESTING_PROMISING`).
              </p>
            </div>

            {/* Real Historical Streak Examples */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 shrink-0">
              {(report?.cps_dynamics?.examples || []).slice(0, 4).map((ex, idx) => (
                <div key={`${ex.esp}-${ex.brand}-${idx}`} className="p-3 bg-white rounded-xl border border-indigo-100 shadow-2xs">
                  <div className="text-[10px] font-bold uppercase text-indigo-600">{ex.esp.toUpperCase()}</div>
                  <div className="text-xs font-extrabold text-gray-900 capitalize truncate">{ex.brand}</div>
                  <div className="mt-1 text-[11px] text-rose-600 font-semibold">
                    {ex.zero_streak_before_first_win}x 0-DKK sends first
                  </div>
                  <div className="text-[11px] text-emerald-700 font-bold">
                    → {ex.overall_rps.toFixed(0)} DKK/send ({ex.total_rev_dkk.toFixed(0)} DKK)
                  </div>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Section 3: Multi-Signal Brand Intelligence Table */}
      <Card className="shadow-sm">
        <CardHeader className="bg-gray-50/80 border-b p-4 flex flex-col md:flex-row md:items-center justify-between gap-3">
          <div>
            <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
              <Layers className="w-4 h-4 text-indigo-600" />
              Multi-Signal Brand Intelligence Matrix ({filteredBrands.length} Brands in {geo.toUpperCase()})
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Click any row to inspect per-ESP performance breakdown (FeedBlitz vs. GetResponse vs. Moosend/MailerLite/Brevo).
            </p>
          </div>

          {/* Filter Pills & Search */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center bg-white border border-gray-200 rounded-lg p-0.5 text-xs">
              {[
                { id: 'ALL', label: 'All' },
                { id: 'PROVEN_WINNER', label: '🏆 Winners' },
                { id: 'EVERGREEN_ANCHOR', label: '🌲 Evergreens' },
                { id: 'CPS_TEST', label: '🧪 CPS Test / Lag' },
                { id: 'WEEKEND_SPECIALIST', label: '🌴 Weekend' },
                { id: 'WEEKDAY_FINANCE_ONLY', label: '🏢 Weekday Only' },
                { id: 'WATCH_BLEED', label: '⚠️ Watch / Bleeders' },
              ].map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setTagFilter(tab.id)}
                  className={`px-2.5 py-1 rounded-md font-semibold transition-all cursor-pointer ${
                    tagFilter === tab.id
                      ? 'bg-indigo-600 text-white shadow-2xs'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <select
              value={verticalFilter}
              onChange={(e) => setVerticalFilter(e.target.value)}
              className="bg-white border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs font-medium text-gray-700"
            >
              <option value="ALL">All Verticals</option>
              <option value="finance">Finance</option>
              <option value="insurance">Insurance</option>
              <option value="nutra">Nutra</option>
              <option value="sweepstakes">Sweepstakes</option>
              <option value="energy">Energy</option>
            </select>

            <div className="relative">
              <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search brand or guidance..."
                className="pl-8 pr-3 py-1.5 text-xs border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400"
              />
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-0 overflow-x-auto">
          {loading ? (
            <div className="p-12 text-center text-gray-500 flex flex-col items-center gap-2">
              <RefreshCw className="w-6 h-6 text-indigo-600 animate-spin" />
              <span className="text-sm font-medium">Loading 120-day Multi-Signal Intelligence...</span>
            </div>
          ) : filteredBrands.length === 0 ? (
            <div className="p-10 text-center text-gray-500 text-sm">
              No brands match the current filter.
            </div>
          ) : (
            <table className="w-full text-xs text-left">
              <thead className="bg-gray-50 text-gray-600 uppercase text-[10px] font-bold border-b">
                <tr>
                  <th className="px-4 py-3">Brand & Payout Model</th>
                  <th className="px-4 py-3">Multi-Signal Status & Day Affinity</th>
                  <th className="px-4 py-3">120d Overall RPS & Hit Rate</th>
                  <th className="px-4 py-3">Weekday vs. Weekend RPS</th>
                  <th className="px-4 py-3">Avg Payout & Lag</th>
                  <th className="px-4 py-3">14d Click → App Funnel</th>
                  <th className="px-4 py-3">AI Scheduler Guidance</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredBrands.map((b) => {
                  const isExpanded = expandedBrand === b.brand;
                  const modelBadge = getPayoutModelBadge(b.payout_model);
                  const espEntries = Object.entries(b.by_esp || {});
                  return (
                    <React.Fragment key={b.brand}>
                      <tr
                        onClick={() => setExpandedBrand(isExpanded ? null : b.brand)}
                        className="hover:bg-indigo-50/30 cursor-pointer transition-colors"
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5">
                            {espEntries.length > 0 ? (
                              isExpanded ? (
                                <ChevronDown className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                              ) : (
                                <ChevronRight className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                              )
                            ) : null}
                            <div>
                              <div className="font-bold text-gray-900 capitalize text-sm flex items-center gap-1.5">
                                {b.brand}
                                <span className="text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">
                                  {b.vertical}
                                </span>
                              </div>
                              <span className={`inline-block mt-1 px-2 py-0.5 rounded text-[10px] font-semibold border ${modelBadge.cls}`}>
                                {modelBadge.label}
                              </span>
                            </div>
                          </div>
                        </td>

                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1 mb-1">
                            {b.status_tags.map((t) => {
                              const tb = getTagBadgeStyle(t);
                              return (
                                <span
                                  key={t}
                                  className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${tb.cls}`}
                                >
                                  {tb.label}
                                </span>
                              );
                            })}
                          </div>
                          <div className="text-[11px] text-gray-600 font-medium flex items-center gap-1">
                            <Clock className="w-3 h-3 text-indigo-500" />
                            <span>Best Slots: <strong className="text-gray-800">{b.day_affinity}</strong></span>
                          </div>
                        </td>

                        <td className="px-4 py-3">
                          <div className="text-sm font-extrabold text-gray-900">
                            {b.rps_120d.toFixed(0)} DKK<span className="text-[10px] font-normal text-gray-500">/send</span>
                          </div>
                          <div className="text-[11px] text-gray-500">
                            <strong>{b.hit_rate_pct.toFixed(0)}%</strong> hit rate ({b.hit_sends_120d}/{b.sends_120d} sends)
                          </div>
                          <div className="text-[10px] text-gray-400">
                            Total: {b.revenue_120d_dkk.toFixed(0)} DKK
                          </div>
                        </td>

                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div>
                              <div className="text-[10px] uppercase text-gray-400 font-bold">Mon–Fri</div>
                              <div className="font-bold text-gray-900">{b.wkday_rps.toFixed(0)} DKK</div>
                              <div className="text-[10px] text-gray-500">{b.wkday_sends}s ({b.wkday_hit_pct.toFixed(0)}%)</div>
                            </div>
                            <div className="h-6 w-px bg-gray-200" />
                            <div>
                              <div className="text-[10px] uppercase text-amber-700 font-bold">Sat–Sun</div>
                              <div className="font-bold text-amber-900">{b.wkend_rps.toFixed(0)} DKK</div>
                              <div className="text-[10px] text-amber-700/80">{b.wkend_sends}s ({b.wkend_hit_pct.toFixed(0)}%)</div>
                            </div>
                          </div>
                        </td>

                        <td className="px-4 py-3">
                          {b.avg_payout_dkk > 0 ? (
                            <>
                              <div className="font-bold text-gray-900">{b.avg_payout_dkk.toFixed(0)} DKK/conv</div>
                              <div className="text-[11px] text-gray-500">
                                Lag: <strong>{b.median_lag_days.toFixed(1)}d</strong> median (p75: {b.p75_lag_days.toFixed(1)}d)
                              </div>
                            </>
                          ) : (
                            <span className="text-gray-400">0 convs yet</span>
                          )}
                        </td>

                        <td className="px-4 py-3">
                          {b.clicks_14d > 0 ? (
                            <>
                              <div className="font-bold text-gray-900">
                                {b.clicks_14d} clk → {b.apps_14d} apps ({b.click_to_app_14d_pct.toFixed(1)}%)
                              </div>
                              <div className="text-[11px] text-gray-500">
                                14d RPS: <strong>{b.rps_14d.toFixed(0)} DKK</strong>
                                {b.apps_14d >= 3 && (
                                  <span className="ml-1.5 text-[10px] text-indigo-700 font-semibold">
                                    (A-B: {b.score_ab_pct.toFixed(0)}% / E-F: {b.score_ef_pct.toFixed(0)}%)
                                  </span>
                                )}
                              </div>
                            </>
                          ) : (
                            <span className="text-gray-400">No sends in last 14d</span>
                          )}
                        </td>

                        <td className="px-4 py-3 max-w-xs">
                          <p className="text-[11px] text-gray-700 leading-snug">{b.scheduler_note}</p>
                        </td>
                      </tr>

                      {isExpanded && espEntries.length > 0 && (
                        <tr className="bg-slate-50/90">
                          <td colSpan={7} className="px-8 py-3">
                            <div className="text-[11px] font-bold text-slate-600 uppercase mb-2">
                              Per-ESP Breakdown for {b.brand.toUpperCase()} (Last 120 Days)
                            </div>
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2.5">
                              {espEntries.map(([espKey, espStat]) => (
                                <div key={espKey} className="p-2.5 bg-white rounded-lg border border-slate-200 shadow-2xs">
                                  <div className="font-bold text-indigo-700 uppercase text-[11px]">{espKey}</div>
                                  <div className="text-sm font-extrabold text-gray-900 mt-0.5">
                                    {espStat.rps.toFixed(0)} DKK/send
                                  </div>
                                  <div className="text-[10px] text-gray-500">
                                    {espStat.sends} sends • {espStat.hit_rate_pct.toFixed(0)}% hit ({espStat.rev_dkk.toFixed(0)} DKK)
                                  </div>
                                  <div className="text-[10px] text-gray-600 mt-1 pt-1 border-t border-gray-100">
                                    Wkday: <strong>{espStat.wkday_rps.toFixed(0)}</strong> • Wkend: <strong>{espStat.wkend_rps.toFixed(0)} DKK</strong>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Section 4: Exact Prompt Preview Sent to Claude Sonnet 4.5 */}
      <Card className="shadow-xs overflow-hidden">
        <div
          onClick={() => setShowPromptPreview(!showPromptPreview)}
          className="bg-gray-50/80 p-4 cursor-pointer flex items-center justify-between hover:bg-gray-100 transition-colors"
        >
          <div className="flex items-center gap-2">
            <Info className="w-4 h-4 text-indigo-600" />
            <h3 className="text-sm font-bold text-gray-800">
              Inspect Live AI Prompt Brief Injected into Claude Sonnet 4.5 ({geo.toUpperCase()})
            </h3>
          </div>
          <span className="text-xs font-bold text-indigo-600">
            {showPromptPreview ? 'Hide Prompt Context' : 'Show Prompt Context'}
          </span>
        </div>
        {showPromptPreview && (
          <CardContent className="p-4 bg-slate-950 text-slate-100 font-mono text-xs overflow-x-auto">
            <pre className="whitespace-pre-wrap leading-relaxed">
              {report?.prompt_preview || 'No prompt preview available.'}
            </pre>
          </CardContent>
        )}
      </Card>
    </div>
  );
}
